import { ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { exhaustMap, from, Subject, Subscription, tap, timer, EMPTY, forkJoin, of, map } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { distinctUntilChanged, filter, takeUntil, catchError, retry } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChatPanelComponent } from '../../../agent/chat-panel/chat-panel.component';
import { Agent } from "../../../../models/users";
import { Campaign } from "../../../../models/campaign";
import { Conversation, ConversationStatus, MessageInterface } from "../../../../models/chat";
import { ChatListPanelComponent } from "../../../agent/chat-list-panel/chat-list-panel.component";
import { SharedDataService } from "../../../../services/shared-data.service";
import { CustomerConversationService } from "../../../../services/customer-conversation.service";
import { CampaignService } from "../../../../services/campaign.service";
import { MessageManagementService } from "../../../../services/message-management.service";
import { ChatNotificationService } from "../../../../services/chat-notification.service";
import { NotificationService } from "../../../../services/notification.service";
import { MessageCommunicationService } from "../../../../services/message-communication.service";
import { ConversationCommunicationService } from "../../../../services/conversation-communication.service";
import { AgentService } from "../services/agent.service";
import { ConversationEventMessage, MessageEventMessage } from "../../../../connectivity";
import { STATUS_OKAY } from "../../../../models/state";
import { environment } from "../../../../environments/enviroment";

@Component({
  selector: 'app-agent-layout',
  imports: [
    CommonModule,
    RouterModule,
    ChatPanelComponent,
    ChatListPanelComponent,
    ButtonModule,
  ],
  animations: [
    trigger('slideDown', [
      state('true', style({ transform: 'translateY(0)', opacity: 1 })),
      state('false', style({ transform: 'translateY(-100%)', opacity: 0 })),
      transition('false => true', animate('300ms ease-out')),
      transition('true => false', animate('300ms ease-in'))
    ])
  ],
  templateUrl: './agent-layout.component.html',
  styleUrl: './agent-layout.component.scss',
  standalone: true
})
export class AgentLayoutComponent implements OnInit, OnDestroy {
  agent: Agent | null = null;
  agentId: string | null = null;

  someRecentChats: Conversation[] = [];
  // Campañas del workspace que un supervisor puede monitorear/asignar. Se cargan
  // una vez al iniciar (user.campaigns viene vacío para supervisores, igual que
  // hace chat-list-panel para mostrar los chats).
  private supervisorCampaignIds: string[] = [];
  selectedConversation: Conversation | null = null;

  campaigns: Campaign[] = [];
  private autoAssignSub!: Subscription;

  /** subject para limpiar monitors */
  private destroy$ = new Subject<void>();

  // Conversation sync monitoring// 5 minutes
  private conversationSyncTimer?: any;
  activeConversationIds = new Set<string>();
  filteredConversations: Conversation[] = []

  // Enhanced connection management
  private isInitialized = false;
  private isInitializing = false;
  chatsWithNewMessage: Record<string, boolean> = {};
  activeConversations: Conversation[] = [];
  inactiveConversations: Conversation[] = [];
  user: Agent | null = null;
  isSupervisor = false;

  messageChat: MessageInterface[] = [];

  // Notification debouncing mechanism
  private lastNotificationTime = 0;
  private readonly NOTIFICATION_DEBOUNCE_TIME = 2000; // 2 seconds debounce
  private notificationTimeouts = new Map<string, any>();
  private currentPlayingAudio: HTMLAudioElement | null = null;

  // Observable subscriptions for communication services
  private conversationEventsSubscription!: Subscription;
  private messageEventsSubscription!: Subscription;

  // WebSocket reconnection management
  private websocketReconnectionSubscription?: Subscription;
  private lastReconnectionTime = 0;
  private readonly RECONNECTION_DEBOUNCE_TIME = 5000; // 5 seconds debounce for reconnection handling

  constructor(
    public renderer: Renderer2,
    public router: Router,
    private sharedDataService: SharedDataService,
    private customerConversationService: CustomerConversationService,
    private campaignService: CampaignService,
    private cdr: ChangeDetectorRef,
    private messageManagementService: MessageManagementService,
    private chatNotificationService: ChatNotificationService,
    private notificationService: NotificationService,
    private http: HttpClient,
    private agentService: AgentService,
    private conversationCommunicationService: ConversationCommunicationService,
    private messageCommunicationService: MessageCommunicationService,
  ) {
    // No special configuration needed for observable services
  }


  async ngOnInit(): Promise<void> {
    // Request notification permission early
    await this.notificationService.requestPermission();
    this.startAutoAssignLoop();

    if (this.isInitialized || this.isInitializing) {
      console.warn('⚠️ Componente ya inicializado o en proceso de inicialización');
      return;
    }

    const storedAgent = localStorage.getItem('logged_in_agent');
    if (storedAgent) {
      this.user = JSON.parse(storedAgent);
      this.isSupervisor = this.user?.role === 'supervisor';
      // Configure conversation communication service to only emit campaigns allowed for this user (helps supervisors)
      try {
        const supervisorCampaigns = this.getSupervisorCampaigns();
        this.conversationCommunicationService.setAllowedCampaigns(this.isSupervisor ? supervisorCampaigns : null);
      } catch (e) {
        console.warn('Unable to set allowed campaigns on ConversationCommunicationService', e);
      }
      // Para supervisores, user.campaigns viene vacío; cargamos las campañas del
      // workspace para que el loop de auto-asignación y los filtros vean los chats.
      if (this.isSupervisor) {
        this.loadSupervisorCampaigns();
      }
    }

    this.isInitializing = true;
    this.isInitialized = true;

    // Get the agent object from the shared data service
    this.agent = this.sharedDataService.getAgent();

    if (this.agent) {
      this.agentId = this.agent.id;

      // Set up conversation selection subscription
      this.setupConversationSelectionSubscription();


      // Initialize with observable services
      this.initializeWithObservableServices();
    } else {
      this.isInitializing = false;
      console.error('❌ No se pudo obtener información del agente');
    }
  }

  /**
   * Initialize the component with observable communication services
   */
  private initializeWithObservableServices(): void {
    try {
      // Step 1: Get conversations
      this.getConversationsAsync();

      // Step 2: Get campaigns
      this.getCampaigns();

      // Step 3: Setup observable subscriptions
      this.setupConversationEventSubscription();
      this.setupMessageEventSubscription();

      console.log('✅ Inicialización con servicios observables completada');
    } catch (error) {
      console.error('❌ Error en inicialización con servicios observables:', error);
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Setup subscription for conversation events from ConversationCommunicationService
   */
  private setupConversationEventSubscription(): void {
    this.conversationEventsSubscription = this.conversationCommunicationService.conversationEvents
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event: ConversationEventMessage) => {
          if (!event || typeof event !== 'object') {
            console.warn('Ignoring invalid conversation payload', event);
            return;
          }

          if ((event as any).type === 'heartbeat') {
            console.debug('Ignoring heartbeat on Conversation WS', event);
            return;
          }

          const conversationId = event.conversation_id ?? event.conversationId;
          if (!conversationId) {
            console.warn('Ignoring invalid conversation payload', event);
            return;
          }
          console.log('📨 Evento de conversación recibido:', event);
          this.handleConversationEvent(event);
        },
        error: (error) => {
          console.error('❌ Error en eventos de conversación:', error);
        }
      });
  }

  /**
   * Setup subscription for message events from MessageCommunicationService
   */
  private setupMessageEventSubscription(): void {
    this.messageEventsSubscription = this.messageCommunicationService.messageEvents
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event: MessageEventMessage) => {
          if (!event || typeof event !== 'object') {
            console.warn('Ignoring invalid message payload', event);
            return;
          }

          if ((event as any).type === 'heartbeat') {
            console.debug('Ignoring heartbeat on Message WS', event);
            return;
          }

          const messageId = event.message_id ?? event.messageId;
          if (!messageId) {
            console.warn('Ignoring invalid message payload', event);
            return;
          }
          console.log('💬 Evento de mensaje recibido:', event);
          this.handleMessageEvent(event);
        },
        error: (error) => {
          console.error('❌ Error en eventos de mensaje:', error);
        }
      });
  }

  /**
   * Handle conversation events from the communication service
   */
  private handleConversationEvent(event: ConversationEventMessage): void {
    if (!event || typeof event !== 'object') {
      console.warn('Ignoring invalid conversation payload', event);
      return;
    }

    if ((event as any).type === 'heartbeat') {
      console.debug('Ignoring heartbeat on Conversation WS', event);
      return;
    }

    const conversationId = event.conversation_id ?? event.conversationId;
    if (!conversationId) {
      console.warn('Ignoring invalid conversation payload', event);
      return;
    }

    // Convert event to conversation model
    const conversation = this.conversationCommunicationService.convertConversationEventMessageToConversation(event);

    if (this.isConversationExcludedForCurrentAgent(conversation)) {
      this.removeConversationFromLocalLists(conversation.conversationId);
      return;
    }

    const canProcessConversationEvent = this.user?.role === 'supervisor' ||
      localStorage.getItem('status_agent') === STATUS_OKAY;

    if (canProcessConversationEvent) {
      console.log('💬 Procesando evento de conversación:', conversation.conversationId);
      this.updateRecentConversation(conversation);
    } else {
      console.warn('⚠️ Agente no está listo para recibir conversaciones');
    }

    // Handle specific conversation states
    this.handleConversationStateChanges(conversation);
    this.cdr.markForCheck();
  }

  /**
   * Handle message events from the communication service
   */
  private handleMessageEvent(event: MessageEventMessage): void {
    if (!event || typeof event !== 'object') {
      console.warn('Ignoring invalid message payload', event);
      return;
    }

    if ((event as any).type === 'heartbeat') {
      console.debug('Ignoring heartbeat on Message WS', event);
      return;
    }

    const messageId = event.message_id ?? event.messageId;
    if (!messageId) {
      console.warn('Ignoring invalid message payload', event);
      return;
    }

    // Convert event to message model
    const message = this.messageCommunicationService.convertMessageEventMessageToMessage(event);

    console.log('💬 Procesando evento de mensaje:', message.conversationId, message.messageType);

    // Update conversation with new message
    this.updateConversationWithNewMessage(message);

    // Handle notifications
    this.handleMessageNotifications(message);

    // Add message to management service
    this.messageManagementService.addMessage(message);

    const conv = this.someRecentChats.find(chat => chat.conversationId === message.conversationId);
    if (!conv) {
      // intentar obtener metadata mínima (si está disponible en el mensaje) o ignorar
      // si el usuario es supervisor y la campaña del message no está en su lista, ignorar
      const campaignId = (message as any).campaignId || null;
      if (!this.isCampaignAllowedForSupervisor(campaignId)) {
        return;
      }
    }
  }

  /**
   * Handle conversation state changes
   */
  private handleConversationStateChanges(conversation: Conversation): void {
    // Update selected conversation if it matches
    if (this.selectedConversation?.conversationId === conversation.conversationId) {
      if (conversation.status === ConversationStatus.Active || conversation.status === ConversationStatus.Pending) {
        this.selectedConversation = conversation;
        console.log('🎯 Conversación seleccionada actualizada');
      } else if (conversation.status === ConversationStatus.Finish) {
        this.selectedConversation = null;
        console.log('❌ Conversación cerrada - selección reset');
      }
    }
    this.cdr.markForCheck();
  }

  /**
   * Handle message notifications with state awareness
   */
  private handleMessageNotifications(message: MessageInterface): void {
    // Only show notifications for messages that are not from the currently selected conversation
    const isFromSelectedConversation = this.selectedConversation &&
      this.selectedConversation.conversationId === message['conversationId'];

    if (!isFromSelectedConversation) {
      // Find the conversation to get full context
      const conversation = this.someRecentChats.find(chat => chat.conversationId === message['conversationId']);

      // Supervisor: ignore messages from campaigns outside their allowed list
      const campaignId = (message as any).campaignId || null;
      if (!this.isCampaignAllowedForSupervisor(campaignId)) {
        console.debug('🔕 Skipping notification: supervisor not allowed for campaign', campaignId);
        return;
      }

      // If conversation is not in the local recent list, skip notification (avoid leaks)
      if (!conversation) {
        console.debug('🔕 Skipping notification: conversation not present in local recent chats', message['conversationId']);
        return;
      }

      // Use state-aware utility to determine if notification should be shown
      const shouldShow = this.notificationService.shouldShowNotificationForState(
        conversation?.status,
        message['status'],
        message['messageType']
      );

      if (shouldShow) {
        this.chatNotificationService.setNewMessage(message['conversationId']);

        // Use the conversation already resolved from recent chats instead of localStorage
        // so supervisors receive notifications even when act_cvns is not populated.
        this.showNotificationForMessage(message, conversation);
        this.playSmartNotification('new_message', message['conversationId']);
      } else {
        console.log('📱 Notification skipped based on state analysis:', {
          conversationStatus: conversation?.status,
          messageStatus: message['status'],
          messageType: message['messageType']
        });
      }
    }
  }

  /**
   * Update conversation with new message from message event
   */
  private updateConversationWithNewMessage(message: MessageInterface): void {
    const conversationIndex = this.someRecentChats.findIndex(
      chat => chat.conversationId === message['conversationId']
    );

    // If conversation not found, ignore messages from conversations outside supervisor's campaigns
    if (conversationIndex === -1) {
      const campaignId = (message as any).campaignId || null;
      if (!this.isCampaignAllowedForSupervisor(campaignId)) {
        // Message belongs to a campaign not visible to this supervisor
        return;
      }
    }

    if (conversationIndex !== -1) {
      // Update conversation to show it has a new message
      const updatedConversation = { ...this.someRecentChats[conversationIndex] };

      // Move conversation to top of list if it's not already there
      if (conversationIndex > 0) {
        this.someRecentChats.splice(conversationIndex, 1);
        this.someRecentChats.unshift(updatedConversation);
      }

      // Play notification sound for new messages
      this.playSmartNotification('new_message', message['conversationId']);

      this.cdr.markForCheck();
    }
  }

  /**
   * Show notification for a new message using standardized notification template
   * Enhanced with memory leak prevention, unified styling, and state awareness
   */
  private async showNotificationForMessage(message: MessageInterface, conversation: any): Promise<void> {
    // Early return if component is being destroyed
    if (this.destroy$.closed) {
      console.log('🛑 Component destroying, skipping notification');
      return;
    }

    try {
      // Try to resolve a full conversation object from local cache if available
      const resolvedConversation = this.someRecentChats.find(c => c.conversationId === message['conversationId']) || conversation;

      // If conversation is not visible to this user (e.g., supervisor and campaign not in list), skip
      if (!this.isConversationVisibleToUser(resolvedConversation)) {
        console.debug('🔕 Suppressing notification: conversation not visible to user', message['conversationId']);
        return;
      }

      const customVars = conversation?.customVariables || {};
      const nombre = customVars['Nombres'] || 'Usuario';
      const apellido = customVars['Apellidos'] || '';
      const campaña = customVars['Campaña'] || 'General';
      const fullName = `${nombre} ${apellido}`.trim();

      // Create enhanced message notification data with state awareness
      const messageNotificationData = {
        senderName: fullName,
        campaignName: campaña,
        messagePreview: message['content'] || 'Nuevo mensaje recibido',
        conversationId: message['conversationId'],
        messageType: message['messageType'],
        messageStatus: message['status'],
        conversationStatus: conversation?.status
      };

      // Use standardized notification method with memory leak protection
      const notificationPromise = this.notificationService.showMessageNotification(messageNotificationData);

      // Race the notification with component destruction
      const notificationShown = await Promise.race([
        notificationPromise,
        new Promise<boolean>((resolve) => {
          this.destroy$.subscribe(() => resolve(false));
        })
      ]);

      if (notificationShown && !this.destroy$.closed) {
        console.log('📱 State-aware message notification shown successfully');
      } else {
        console.log('📱 MessageInterface notification was not shown (component destroyed or failed)');
      }
    } catch (error) {
      if (!this.destroy$.closed) {
        console.error('❌ Error showing state-aware message notification:', error);
      }
    }
  }

  /**
   * Set up subscription to listen for conversation selection changes from ChatListPanelComponent
   */
  private setupConversationSelectionSubscription(): void {
    // Subscribe to conversation selection changes from CustomerConversationService
    this.customerConversationService.conversation$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev?.conversationId === curr?.conversationId)
      )
      .subscribe({
        next: (conversation: Conversation | null) => {
          this.handleConversationSelection(conversation);
        },
        error: (error) => {
          console.error('❌ Error en suscripción de selección de conversación:', error);
        }
      });
  }

  /**
   * Handle conversation selection from ChatListPanelComponent
   */
  private handleConversationSelection(conversation: Conversation | null): void {
    console.log('📥 Manejando selección de conversación:', conversation?.conversationId);

    // Update the selected conversation
    this.selectedConversation = conversation;

    // Clear chat notification for the selected conversation
    if (conversation?.conversationId) {
      this.chatNotificationService.clearNewMessage(conversation.conversationId);
    }

    // Trigger change detection to update the UI
    this.cdr.markForCheck();
  }

  /**
   * Enhanced async conversation loading with memory leak prevention
   */
  private async getConversationsAsync(): Promise<void> {
    // Early return if component is being destroyed
    if (this.destroy$.closed) {
      console.log('🛑 Component destroying, skipping conversation loading');
      return Promise.resolve();
    }

    const user = this.sharedDataService.getAgent();
    return new Promise((resolve, reject) => {
      // Set up a timeout to prevent hanging promises
      const timeoutId = setTimeout(() => {
        reject(new Error('Conversation loading timeout'));
      }, 30000); // 30 second timeout

      if (localStorage.getItem('status_agent') === STATUS_OKAY || user?.role !== "agent") {
        this.customerConversationService.getConversationAvailableAgent(
          this.agent?.id || ''
        ).pipe(
          takeUntil(this.destroy$), // Cancel if component is destroyed
          catchError(error => {
            clearTimeout(timeoutId);
            if (!this.destroy$.closed) {
              console.error('❌ Error loading conversations:', error);
              reject(error);
            }
            return EMPTY;
          })
        ).subscribe({
          next: (conversations: Conversation[]) => {
            const supervisorCampaigns = this.getSupervisorCampaigns();
            const visible = this.user?.role === 'supervisor'
              ? conversations.filter(c => c?.campaignId ? supervisorCampaigns.includes(String(c.campaignId)) : false)
              : conversations;
            clearTimeout(timeoutId);
            if (!this.destroy$.closed) {
              this.someRecentChats = this.mergeAndSortConversations(visible, this.someRecentChats || []);
              resolve();
            } else {
              // Component was destroyed during the operation
              resolve();
            }
          },
          error: (error) => {
            clearTimeout(timeoutId);
            if (!this.destroy$.closed) {
              reject(error);
            } else {
              resolve(); // Don't reject if component is destroyed
            }
          }
        });
      } else {
        clearTimeout(timeoutId);
        resolve();
      }
    });
  }

  private startAutoAssignLoop(): void {
    this.autoAssignSub = timer(0, 7000).pipe(
      takeUntil(this.destroy$),
      exhaustMap(() => {
        // this.setupMessageEventSubscription();

        // Contain failures to a single tick: if getConversationsAsync() fails
        // (timeout, network/proxy error, 4xx/5xx) swallow the error here so the
        // timer keeps emitting. Without this catchError a single failure
        // terminates the whole stream and auto-assignment stops until reload.
        return from(this.getConversationsAsync()).pipe(
          catchError(error => {
            if (!this.destroy$.closed) {
              console.error('❌ Error en ciclo de auto-asignación (se reintenta en el próximo tick):', error);
            }
            return EMPTY;
          })
        );
      }),
      tap(() => console.log(`🔁 [auto-asignación] tick del timer — rol=${this.user?.role}, chats en lista=${this.someRecentChats?.length ?? 0}`)),
      // Corre para agentes y también para supervisores que estén monitoreando,
      // así los chats Pending se asignan aunque solo haya un supervisor con la
      // app abierta. El supervisor nunca es candidato a recibir el chat porque
      // automaticAssign solo asigna a agentes de order_priority_agent.
      filter(() => this.user?.role === 'agent' || this.user?.role === 'supervisor'),
      filter(() => (this.someRecentChats?.length ?? 0) > 0)
    )
      .subscribe(() => {
        const pending = this.someRecentChats.filter(conv => conv.status === ConversationStatus.Pending);
        console.log(`🔄 [auto-asignación] tick ejecutado — ${pending.length} chat(s) Pending de ${this.someRecentChats.length} en lista`);
        pending.forEach(conv => this.automaticAssign(conv));
      });
  }


  /**
   * Enhanced automatic assignment with memory leak prevention
   */
  private automaticAssign(conversation: Conversation): void {
    const conversationId = conversation?.conversationId;
    if (this.destroy$.closed || !conversationId) {
      return;
    }

    // The local list can be stale while another flow closes a conversation.
    // Confirm the authoritative status before any auto-assignment request.
    this.customerConversationService.getConversationById(conversationId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: currentConversation => {
        const status = String(currentConversation?.status ?? '').toLowerCase();
        if (status !== ConversationStatus.Pending.toLowerCase()) {
          if (status === ConversationStatus.Finish.toLowerCase()) {
            this.removeConversationFromLocalLists(conversationId);
            console.info(`⏭️ [auto-asignación] conversación ${conversationId} cerrada; no se asigna`);
          } else {
            console.info(`⏭️ [auto-asignación] conversación ${conversationId} ya no está pendiente (${currentConversation?.status}); no se asigna`);
          }
          return;
        }

        this.assignPendingConversationAutomatically({
          ...conversation,
          ...currentConversation,
          conversationId: currentConversation?.conversationId ?? conversationId,
          agentId: currentConversation?.agentId ?? conversation.agentId,
          status: ConversationStatus.Pending
        });
      },
      error: error => {
        if (error?.status === 404) {
          this.removeConversationFromLocalLists(conversationId);
          console.info(`⏭️ [auto-asignación] conversación ${conversationId} no disponible; no se asigna`);
          return;
        }

        console.error(`❌ [auto-asignación] no fue posible validar la conversación ${conversationId}; se omite la asignación`, error);
      }
    });
  }

  private assignPendingConversationAutomatically(conversation: Conversation): void {
    // Early return if component is being destroyed
    if (this.destroy$.closed) {
      return;
    }

    const token = localStorage.getItem('auth_req');
    const campaignId = conversation.campaignId;
    if (!campaignId || !token) {
      return;
    }

    console.log(`▶️ [auto-asignación] automaticAssign() ejecutándose para conversación ${conversation.conversationId} (campaña ${campaignId})`);

    const url = `${environment.mindxbkUrl}ConsultPriorityAdvisors/${campaignId}`;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });

    this.http.get<{
      campaing_id: string;
      campaing_name: string;
      campaing_priority: string;
      agents: { agent_id: string; priority: number }[];
      ratio: string;
    }>(url, { headers }).pipe(
      takeUntil(this.destroy$), // Cancel request if component is destroyed
      catchError(error => {
        if (!this.destroy$.closed) {
          console.error('❌ Error al consultar prioridad de agentes:', error);
        }
        return EMPTY; // Return empty observable on error
      })
    ).subscribe({
      next: data => {
        // Check if component is still alive before processing
        if (this.destroy$.closed) {
          return;
        }

        // Parseo ratio y ordeno lista de agentes por prioridad ascendente
        const [_, maxStr] = data.ratio.split(':');
        const maxConvs = parseInt(maxStr, 10) || 1;

        // Primero obtenemos la carga actual de todos los agentes
        const agentLoadPromises = data.agents.map(agent =>
          this.customerConversationService.getActiveConversations(agent.agent_id).pipe(
            // Reintenta blips transitorios (red/timeout/5xx) antes de rendirse,
            // para no marcar a un agente como "lleno" por un error puntual.
            retry({ count: 2, delay: 500 }),
            map(convs => ({
              agent_id: agent.agent_id,
              priority: agent.priority,
              currentLoad: convs.filter((c: any) => c.status === ConversationStatus.Active).length,
              maxLoad: maxConvs,
              loadPercentage: (convs.filter((c: any) => c.status === ConversationStatus.Active).length / maxConvs) * 100
            })),
            catchError(err => {
              console.error(`❌ Error obteniendo carga de agente ${agent.agent_id}:`, err);

              return of({
                agent_id: agent.agent_id,
                priority: agent.priority,
                currentLoad: maxConvs, // Asumir lleno si hay error
                maxLoad: maxConvs,
                loadPercentage: 100
              });
            })
          )
        );

        // Esperar todas las cargas y procesar
        forkJoin(agentLoadPromises).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (agentLoads) => {
            if (this.destroy$.closed) {
              return;
            }

            // Filtrar agentes disponibles (que no estén llenos)
            const availableAgents = agentLoads.filter(agent => agent.currentLoad < agent.maxLoad);

            if (availableAgents.length === 0) {
              return;
            }

            // Ordenar por: 1) Prioridad, 2) Menor carga actual, 3) Aleatorio
            const sortedAgents = availableAgents.sort((a, b) => {
              // 1. Prioridad (menor número = mayor prioridad)
              if (a.priority !== b.priority) {
                return a.priority - b.priority;
              }

              // 2. Menor carga actual (distribución equitativa)
              if (a.currentLoad !== b.currentLoad) {
                return a.currentLoad - b.currentLoad;
              }

              // 3. Aleatorio si tienen misma prioridad y carga
              return Math.random() - 0.5;
            });

            // Intentar asignar al mejor agente
            const selectedAgent = sortedAgents[0];
            this.assignConversationToSelectedAgent(conversation, selectedAgent, token);
          },
          error: (error) => {
            if (!this.destroy$.closed) {
              console.error('❌ Error calculando cargas de agentes:', error);
            }
          }
        });
      }
    });
  }

  /**
   * Asignar conversación a un agente específico seleccionado por el algoritmo de distribución equitativa
   */
  private assignConversationToSelectedAgent(conversation: Conversation, selectedAgent: any, token: string): void {
    if (this.destroy$.closed) {
      return;
    }

    // Verificar que la conversación no esté ya asignada
    this.agentService.isConversationAlreadySelected(conversation.conversationId, token)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          if (!this.destroy$.closed) {
            console.error('❌ Error comprobando asignación previa:', err);
          }
          return EMPTY;
        })
      )
      .subscribe({
        next: (sel: any) => {
          if (this.destroy$.closed) {
            return;
          }

          if (sel.agentId) {
            // La conversación ya está asignada.
            if (this.user?.role === 'agent' && sel.agentId !== this.agent?.id) {
              // Solo para un AGENTE: si la tomó otro agente, la quitamos de su
              // lista local para que salga de "Sin asignar". El merge del
              // refresco preserva ítems locales que el servidor ya no devuelve,
              // así que sin esto la copia obsoleta queda pegada como pendiente.
              // Un SUPERVISOR que monitorea NO la quita: debe seguir viéndola.
              this.someRecentChats = this.someRecentChats.filter(
                c => c.conversationId !== conversation.conversationId
              );
              if (!this.destroy$.closed) {
                this.cdr.markForCheck();
              }
              console.log(`⏭️ [auto-asignación] conversación ${conversation.conversationId} ya fue tomada por otro agente (${sel.agentId}); se quita de "Sin asignar" en este panel`);
            } else {
              console.log(`⏭️ [auto-asignación] conversación ${conversation.conversationId} ya estaba asignada a ${sel.agentId}, se omite`);
            }
          } else {
            // Asignar la conversación
            const body = {
              conversationId: conversation.conversationId,
              agentId: selectedAgent.agent_id,
              status: ConversationStatus.Active
            };

            console.log(`✅ [auto-asignación] asignando conversación ${conversation.conversationId} al agente ${selectedAgent.agent_id} (prioridad ${selectedAgent.priority}, carga ${selectedAgent.currentLoad}/${selectedAgent.maxLoad})`);

            this.agentService.setAgentConversation(body, token).pipe(
              takeUntil(this.destroy$),
              catchError(err => {
                if (!this.destroy$.closed) {
                  console.error('❌ Error al asignar conversación:', err);
                }
                return EMPTY;
              })
            ).subscribe({
              complete: () => {
                if (!this.destroy$.closed) {
                  // Refrescar la lista de conversaciones
                  this.getConversationsAsync().then(r => {
                    // Lista actualizada
                  }).catch(e => {
                    if (!this.destroy$.closed) {
                      console.error('Error refreshing conversations:', e);
                    }
                  });
                }
              }
            });
          }
        }
      });
  }

  private getActiveConversationsFromStorage(): { conversationId: string }[] {
    try {
      const raw = localStorage.getItem('act_cvns');
      if (!raw) return [];
      return JSON.parse(raw);
    } catch (e) {
      console.warn('❌ Error leyendo act_cvns del localStorage', e);
      return [];
    }
  }

  /**
   * Merge server-provided conversations with local list preserving local-only items
   * (e.g., recently added pending conversations) and return a stable sorted array.
   */
  private mergeAndSortConversations(server: Conversation[], local: Conversation[]): Conversation[] {
    const map = new Map<string, Conversation>();

    // Add server items first (server authoritative when present)
    for (const c of server || []) {
      if (c?.conversationId) map.set(c.conversationId, c);
    }

    // Preserve local items that server didn't return (recently created or transient)
    for (const c of local || []) {
      if (!c || !c.conversationId) continue;
      if (!map.has(c.conversationId)) {
        map.set(c.conversationId, c);
      }
    }

    const merged = Array.from(map.values()).filter(conversation =>
      !this.isConversationExcludedForCurrentAgent(conversation)
    );

    // Sort: Pending first, Active second, Finish last; within each group by date desc
    const statusOrder: Record<string, number> = {
      [ConversationStatus.Pending]: 1,
      [ConversationStatus.Active]: 2,
      [ConversationStatus.Finish]: 3
    } as any;

    merged.sort((a, b) => {
      const orderA = statusOrder[a.status] || 99;
      const orderB = statusOrder[b.status] || 99;
      if (orderA !== orderB) return orderA - orderB;
      const dateA = a.status === ConversationStatus.Active && a.acceptedDate ? a.acceptedDate : a.startDate;
      const dateB = b.status === ConversationStatus.Active && b.acceptedDate ? b.acceptedDate : b.startDate;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    return merged;
  }

  /**
   * Get the active conversation for the agent.
   */
  getConversations(): void {
    console.log('🔄 Fetching conversations for agent:', this.agent?.id);
    this.customerConversationService.getConversationAvailableAgent(
      this.agent?.id || ''
    ).subscribe(
      {
        next: (conversations: Conversation[]) => {
          // Apply supervisor visibility filter if needed
          const supervisorCampaigns = this.getSupervisorCampaigns();
          const visible = this.user?.role === 'supervisor'
            ? conversations.filter(c => c?.campaignId ? supervisorCampaigns.includes(String(c.campaignId)) : false)
            : conversations;
          this.someRecentChats = visible;
        }
      }
    );
  }

  /**
   * Assigns the agent to the conversation, updates the status using the conversation communication service.
   */
  assignConversationToAgent(conversation: Conversation): void {
    if (!this.agentId) {
      console.error('❌ ID de agente faltante. No se puede asignar conversación.');
      return;
    }

    if (!conversation || !conversation.conversationId) {
      console.error('❌ Objeto de conversación inválido.');
      return;
    }

    const updatedConversation: Conversation = {
      ...conversation,
      agentId: this.agentId,
      status: ConversationStatus.Active
    };

    console.log('👤 Asignando conversación al agente:', {
      conversationId: updatedConversation.conversationId,
      agentId: this.agentId,
      status: updatedConversation.status
    });

    // Convert conversation to event message format and emit
    const conversationEvent: ConversationEventMessage = {
      event_type: 'conversation_event',
      conversation_id: conversation.conversationId,
      key_prefix: conversation.keyPrefix || '',
      start_date: conversation.startDate?.toISOString() || new Date().toISOString(),
      client_id: conversation.clientId,
      channel_id: conversation.channelId,
      platform: conversation.platform,
      agent_id: this.agentId,
      agent_name: this.agent?.username,
      status: ConversationStatus.Active,
      end_date: conversation.endDate?.toISOString() || null,
      accepted_date: new Date().toISOString(),
      campaign_id: conversation.campaignId,
      ticket_id: conversation.ticketId,
      metadata: conversation.metadata ? {
        general_config: conversation.metadata.getgeneralConfig ? {
          accept_conversation: conversation.metadata.getgeneralConfig.accept_conversation,
          enable_file_upload: conversation.metadata.getgeneralConfig.enable_file_upload,
          enable_file_download: conversation.metadata.getgeneralConfig.enable_file_download
        } : undefined,
        translation_config: conversation.metadata.translationConfig ? {
          enable: conversation.metadata.translationConfig.enable,
          source_language: conversation.metadata.translationConfig.sourceLanguage || null,
          target_language: conversation.metadata.translationConfig.targetLanguage || null
        } : undefined
      } : undefined,
      custom_variables: conversation.customVariables,
      timestamp: new Date().toISOString()
    };

    // Emit the conversation event
    this.conversationCommunicationService.emitConversationEvent(conversationEvent);
    console.log('✅ Asignación de conversación emitida a través del servicio de comunicación');
  }

  /**
   * Smart notification handler using standardized templates
   * Enhanced with memory leak prevention and unified styling
   * @param type - Type of notification (new_conversation, new_message, conversation_update)
   * @param conversationId - Optional conversation ID for tracking
   */
  private playSmartNotification(type: 'new_conversation' | 'new_message' | 'conversation_update', conversationId?: string): void {
    // Early return if component is being destroyed
    if (this.destroy$.closed) {
      console.log('🛑 Component destroying, skipping smart notification');
      return;
    }

    const notificationKey = conversationId ? `${type}_${conversationId}` : type;

    // If we have a timeout for this specific notification, clear it
    if (this.notificationTimeouts.has(notificationKey)) {
      const timeoutId = this.notificationTimeouts.get(notificationKey);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.notificationTimeouts.delete(notificationKey);
      }
    }

    // Execute notification with standardized templates
    const executeNotification = async () => {
      if (this.destroy$.closed) {
        console.log('🛑 Component destroyed during notification execution');
        return;
      }

      try {
        // Play sound first
        this.playNotificationSound();

        // Only show notification if component is still alive
        if (!this.destroy$.closed) {
          // Find the conversation for better context
          const conversation = conversationId ?
            this.someRecentChats.find(chat => chat.conversationId === conversationId) : null;

          // If user is supervisor and conversation isn't visible to them, skip notification
          if (this.user?.role === 'supervisor') {
            if (!conversation) {
              console.debug('🔕 Skipping executeNotification: conversation not in local cache for supervisor', conversationId);
              return;
            }
            if (!this.isConversationVisibleToUser(conversation)) {
              console.debug('🔕 Skipping executeNotification: conversation not visible to supervisor', conversationId);
              return;
            }
          }

          const customVars = conversation?.customVariables || {};
          const clientName = customVars['Nombres'] ?
            `${customVars['Nombres']} ${customVars['Apellidos'] || ''}`.trim() : 'Cliente';
          const campaignName = customVars['Campaña'] || 'General';

          switch (type) {
            case 'new_conversation':
              const conversationData = {
                campaignName,
                clientName,
                conversationId: conversationId || 'unknown',
                status: ConversationStatus.Active,
                previousStatus: ConversationStatus.Pending
              };
              await this.notificationService.showConversationNotification(conversationData);
              break;

            case 'new_message':
              // For new_message, we typically handle this in showNotificationForMessage
              // But this is a fallback for cases where we don't have full message context
              const messageData = {
                senderName: clientName,
                campaignName,
                messagePreview: 'Nuevo mensaje recibido',
                conversationId: conversationId || 'unknown'
              };
              await this.notificationService.showMessageNotification(messageData);
              break;

            case 'conversation_update':
              const updateData = {
                campaignName,
                clientName,
                conversationId: conversationId || 'unknown',
                status: ConversationStatus.Active
              };
              await this.notificationService.showConversationNotification(updateData);
              break;
          }
        }
      } catch (error) {
        if (!this.destroy$.closed) {
          console.error('❌ Error in standardized notification execution:', error);
        }
      } finally {
        // Clean up the timeout reference
        this.notificationTimeouts.delete(notificationKey);
      }
    };

    // Execute notification immediately (no debouncing to prevent memory leaks)
    executeNotification();
  }

  /**
   * Play notification sound with memory leak prevention
   */
  playNotificationSound(): void {
    // Early return if component is being destroyed
    if (this.destroy$.closed) {
      console.log('🛑 Component destroying, skipping notification sound');
      return;
    }

    // Debounce notification sounds to prevent repetitive playing
    const now = Date.now();
    if (now - this.lastNotificationTime < this.NOTIFICATION_DEBOUNCE_TIME) {
      console.log('🔇 Notification sound debounced - too soon since last notification');
      return;
    }

    try {
      // Stop and remove previous audio listeners safely
      if (this.currentPlayingAudio) {
        try {
          const prev = this.currentPlayingAudio as any;
          // If handlers were stored on the element, remove them by reference
          if (prev.__endedHandler) prev.removeEventListener('ended', prev.__endedHandler);
          if (prev.__errorHandler) prev.removeEventListener('error', prev.__errorHandler);
        } catch (remErr) {
          // ignore removal errors
        }
        try {
          this.currentPlayingAudio.pause();
          this.currentPlayingAudio.currentTime = 0;
        } catch (pauseErr) {
          // ignore
        }
        this.currentPlayingAudio = null;
      }

      this.lastNotificationTime = now;

      // Create audio element and set conservative playback options
      const audio = new Audio('assets/sound/notification.mp3');
      audio.preload = 'auto';
      audio.loop = false;

      // Bind handlers and attach them to the element so we can remove by reference later
      const endedHandler = () => this.onAudioEnded();
      const errorHandler = (e: any) => this.onAudioError(e);
      // Store references on element to ensure correct removal
      (audio as any).__endedHandler = endedHandler;
      (audio as any).__errorHandler = errorHandler;

      audio.addEventListener('ended', endedHandler);
      audio.addEventListener('error', errorHandler);

      // Keep component-level refs for potential external cleanup
      this.audioEndedHandler = endedHandler;
      this.audioErrorHandler = errorHandler;
      this.currentPlayingAudio = audio;

      // Attempt to play and handle promise rejections
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          if (!this.destroy$.closed) {
            console.error('Error al reproducir el sonido:', err);
          }
          this.cleanupAudio();
        });
      }

      // Safety timeout to clean up audio after maximum expected duration
      setTimeout(() => {
        if (this.currentPlayingAudio && !this.destroy$.closed) {
          this.cleanupAudio();
        }
      }, 5000); // 5 second safety timeout

    } catch (error) {
      if (!this.destroy$.closed) {
        console.error('Error creating notification audio:', error);
      }
      this.cleanupAudio();
    }
  }

  /**
   * Handle audio ended event
   */
  private onAudioEnded(): void {
    this.cleanupAudio();
  }

  /**
   * Handle audio error event
   */
  private onAudioError(error: any): void {
    if (!this.destroy$.closed) {
      console.error('Audio playback error:', error);
    }
    this.cleanupAudio();
  }

  /**
   * Clean up audio resources
   */
  private cleanupAudio(): void {
    if (this.currentPlayingAudio) {
      try {
        const audio = this.currentPlayingAudio as any;
        // Prefer removing handlers stored on the element
        if (audio.__endedHandler) {
          try { audio.removeEventListener('ended', audio.__endedHandler); } catch (e) { }
          delete audio.__endedHandler;
        } else if (this.audioEndedHandler) {
          try { audio.removeEventListener('ended', this.audioEndedHandler); } catch (e) { }
        }

        if (audio.__errorHandler) {
          try { audio.removeEventListener('error', audio.__errorHandler); } catch (e) { }
          delete audio.__errorHandler;
        } else if (this.audioErrorHandler) {
          try { audio.removeEventListener('error', this.audioErrorHandler); } catch (e) { }
        }

        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (err) {
          // ignore
        }
      } catch (error) {
        // Ignore cleanup errors
      }
      this.currentPlayingAudio = null;
      this.audioEndedHandler = undefined;
      this.audioErrorHandler = undefined;
    }
  }

  // Add these properties to store the bound methods (optional to allow safe cleanup)
  private audioEndedHandler?: () => void;
  private audioErrorHandler?: (error: any) => void;

  /**
   * Update recent chats list when receiving new conversations from WebSocket.
   * Enhanced with memory leak prevention
   */
  private updateRecentConversation(newConversation: Conversation): void {
    // Early return if component is being destroyed
    if (this.destroy$.closed) {
      console.log('🛑 Component destroying, skipping conversation update');
      return;
    }

    if (!this.isConversationVisibleToUser(newConversation)) {
      console.debug('Supervisor: conversación fuera de alcance, ignorando', newConversation.conversationId);
      return;
    }

    try {
      if (this.isConversationExcludedForCurrentAgent(newConversation)) {
        this.removeConversationFromLocalLists(newConversation.conversationId);
        return;
      }

      const index = this.someRecentChats.findIndex(chat => chat.conversationId === newConversation.conversationId);
      console.log('🔄 Actualizando conversación reciente:', newConversation);

      // Only play notification if component is still alive
      if (!this.destroy$.closed) {
        this.playSmartNotification('conversation_update', newConversation.conversationId);
      }

      if (index !== -1) {
        // Update existing conversation
        this.someRecentChats[index] = newConversation;
        this.someRecentChats = [...this.someRecentChats];

        console.log('🔄 Conversación actualizada en la lista de chats recientes:', newConversation);

        // Update selected conversation if it matches and component is alive
        if (!this.destroy$.closed && this.selectedConversation?.conversationId === newConversation.conversationId) {
          if (newConversation.status === ConversationStatus.Active || newConversation.status === ConversationStatus.Pending) {
            this.selectedConversation = newConversation;
            console.log('🔄 Conversación seleccionada actualizada:', newConversation.conversationId);
          } else if (newConversation.status === ConversationStatus.Finish) {
            this.selectedConversation = null;
            console.log('🔄 Conversación finalizada, deseleccionando:', newConversation.conversationId);
          }
        }
      } else {
        // Add new conversation only if it's pending and component is alive
        if (!this.destroy$.closed && newConversation.status === ConversationStatus.Pending) {
          this.someRecentChats = [newConversation, ...this.someRecentChats];
          console.log('➕ Nueva conversación añadida a la lista');
        }
      }

      // Trigger change detection only if component is still alive
      if (!this.destroy$.closed) {
        this.cdr.markForCheck();
      }
    } catch (error) {
      if (!this.destroy$.closed) {
        console.error('❌ Error updating recent conversation:', error);
      }
    }
  }


  /**
   * Clean up subscriptions and resources to prevent memory leaks.
   * Enhanced with comprehensive memory leak prevention
   */
  ngOnDestroy(): void {
    console.log('🧹 AgentLayout: Iniciando limpieza completa de recursos...');

    // Mark component as being destroyed FIRST to prevent new operations
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }

    // Clear debounce timers and reset reconnection state
    this.lastReconnectionTime = 0;
    this.lastNotificationTime = 0;

    // Mark component flags for cleanup
    this.isInitialized = false;
    this.isInitializing = false;

    // Clear conversation sync timer
    if (this.conversationSyncTimer) {
      clearInterval(this.conversationSyncTimer);
      this.conversationSyncTimer = null;
    }

    // Clear ALL notification timeouts with enhanced cleanup
    if (this.notificationTimeouts && this.notificationTimeouts.size > 0) {
      this.notificationTimeouts.forEach((timeoutId, key) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      });
      this.notificationTimeouts.clear();
    }

    // Clean up audio resources thoroughly
    this.cleanupAudio();

    // Clean up notification services with error handling
    try {
      if (this.chatNotificationService && typeof this.chatNotificationService.cleanup === 'function') {
        this.chatNotificationService.cleanup();
      }
    } catch (error) {
      console.warn('⚠️ Error cleaning up chat notification service:', error);
    }

    // Unsubscribe from observable service subscriptions with enhanced error handling
    const subscriptions = [
      { sub: this.conversationEventsSubscription, name: 'conversationEvents' },
      { sub: this.messageEventsSubscription, name: 'messageEvents' },
      { sub: this.websocketReconnectionSubscription, name: 'websocketReconnection' },
      { sub: this.autoAssignSub, name: 'autoAssign' }
    ];

    subscriptions.forEach(({ sub, name }) => {
      if (sub && !sub.closed) {
        try {
          sub.unsubscribe();
          console.log(`✅ ${name} subscription unsubscribed`);
        } catch (error) {
          console.warn(`⚠️ Error unsubscribing ${name} subscription:`, error);
        }
      }
    });

    // Nullify subscription references to help garbage collection
    this.conversationEventsSubscription = null as any;
    this.messageEventsSubscription = null as any;
    this.websocketReconnectionSubscription = null as any;
    this.autoAssignSub = null as any;

    // Clear all data references to prevent memory leaks
    this.someRecentChats = [];
    this.selectedConversation = null;
    this.campaigns = [];
    this.messageChat = [];
    this.activeConversations = [];
    this.inactiveConversations = [];
    this.filteredConversations = [];
    this.chatsWithNewMessage = {};

    // Clear sets and maps
    if (this.activeConversationIds) {
      this.activeConversationIds.clear();
    }

    // Reset agent references
    this.agent = null;
    this.agentId = null;
    this.user = null;


    // Final cleanup verification
    console.log('✅ AgentLayout: Limpieza completa de recursos completada');

    // Help garbage collection by nullifying major service references
    // Note: Don't nullify injected services as they're managed by Angular DI
    try {
      // Only nullify references that might create circular dependencies
      this.cdr?.detach();
    } catch (error) {
      console.warn('⚠️ Error detaching change detector:', error);
    }
  }


  /**
   * Enhanced campaign loading with memory leak prevention
   */
  private getCampaigns(): void {
    if (!this.agent || this.destroy$.closed) {
      return;
    }

    // Guard against missing campaigns array on agent
    const agentCampaigns = Array.isArray(this.agent?.campaigns) ? this.agent!.campaigns : [];

    this.campaignService.getCampaigns(agentCampaigns).pipe(
      takeUntil(this.destroy$), // Cancel if component is destroyed
      catchError(error => {
        if (!this.destroy$.closed) {
          console.error('❌ Error obteniendo campañas:', error);
        }
        return EMPTY; // Return empty observable on error
      })
    ).subscribe({
      next: (campaigns: Campaign[]) => {
        if (!this.destroy$.closed) {
          this.campaigns = campaigns;
          this.campaignService.setCampaignsStorage(campaigns);

          if (campaigns.length > 0) {
            try {
              localStorage.setItem('rt', campaigns[0].ratio);
              localStorage.setItem('accept_conversation', String(campaigns[0].general_config?.accept_conversation || false));
            } catch (storageError) {
              console.warn('⚠️ Error setting localStorage in getCampaigns:', storageError);
            }
          }
        }
      }
    });
  }

  // Enhanced method to handle conversation selection
  onConversationSelected(conversation: Conversation): void {
    // console.log('🎯 Conversación seleccionada:', conversation.conversationId);
    this.selectedConversation = conversation;
    this.assignConversationToAgent(conversation);
  }

  onConversationUnavailable(conversationId: string): void {
    this.removeConversationFromLocalLists(conversationId);
    this.getConversationsAsync().catch(error => {
      console.error('Error refreshing conversation lists after unavailable conversation:', error);
    });
  }

  private getSupervisorCampaigns(): string[] {
    if (this.user?.role !== 'supervisor') return [];
    // Preferimos las campañas del workspace (cargadas asíncronamente); si aún no
    // están, caemos a user.campaigns (que para supervisores suele venir vacío).
    if (this.supervisorCampaignIds.length > 0) return this.supervisorCampaignIds;
    const campaigns = (this.user as any)?.campaigns;
    return Array.isArray(campaigns) ? campaigns.map(String) : [];
  }

  private getWorkspaceIdFromStorage(): string {
    try {
      const raw = localStorage.getItem('workspace')
        || localStorage.getItem('workspaceId')
        || localStorage.getItem('workpsaceId');
      if (!raw) return '';
      const ws = JSON.parse(raw);
      return String(ws?.id || ws?.workspaceId || '');
    } catch {
      return '';
    }
  }

  private loadSupervisorCampaigns(): void {
    if (this.user?.role !== 'supervisor') return;
    const workspaceId = this.getWorkspaceIdFromStorage();
    if (!workspaceId) {
      console.warn('🧭 [auto-asignación] supervisor sin workspaceId en storage; no se cargan campañas');
      return;
    }
    this.campaignService.getCampaignsByWorkspaceId(workspaceId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (campaigns: any[]) => {
        this.supervisorCampaignIds = (campaigns || []).map(c => c?.id).filter(Boolean);
        console.log(`🧭 [auto-asignación] campañas del supervisor (workspace) cargadas: ${this.supervisorCampaignIds.length}`);
        // Reaplicar el filtro de eventos de websocket con las campañas correctas.
        try {
          this.conversationCommunicationService.setAllowedCampaigns(this.supervisorCampaignIds);
        } catch (e) { /* noop */ }
        // Refrescar la lista ya con el filtro correcto.
        this.getConversationsAsync().catch(() => { });
      },
      error: err => console.error('Error cargando campañas del supervisor:', err)
    });
  }

  private isCampaignAllowedForSupervisor(campaignId?: string | null): boolean {
    if (this.user?.role !== 'supervisor') return true;
    if (!campaignId) return false;
    return this.getSupervisorCampaigns().includes(String(campaignId));
  }

  private isConversationVisibleToUser(conv?: Conversation | null): boolean {
    // If we don't have user info, assume not visible
    if (!this.user) return false;

    // Non-supervisors can see everything
    if (this.user.role !== 'supervisor') return true;

    // Supervisor: require a conversation object with campaignId
    if (!conv || !conv.campaignId) return false;
    return this.isCampaignAllowedForSupervisor(conv.campaignId);
  }

  private isConversationExcludedForCurrentAgent(conversation?: Conversation | null): boolean {
    if (!conversation || this.user?.role !== 'agent') {
      return false;
    }

    const currentAgentId = String(this.agent?.id ?? this.user?.id ?? '').trim();
    if (!currentAgentId) {
      return true;
    }

    if (conversation.status === ConversationStatus.Active) {
      return String(conversation.agentId ?? '').trim() !== currentAgentId;
    }

    if (conversation.status === ConversationStatus.Pending) {
      const allowedCampaigns = Array.isArray(this.agent?.campaigns)
        ? this.agent.campaigns.map(String)
        : [];
      return !conversation.campaignId || !allowedCampaigns.includes(String(conversation.campaignId));
    }

    return false;
  }

  private removeConversationFromLocalLists(conversationId?: string | null): void {
    if (!conversationId) {
      return;
    }

    const removeConversation = (conversation: Conversation) => conversation.conversationId !== conversationId;
    this.someRecentChats = this.someRecentChats.filter(removeConversation);
    this.activeConversations = this.activeConversations.filter(removeConversation);
    this.inactiveConversations = this.inactiveConversations.filter(removeConversation);
    this.filteredConversations = this.filteredConversations.filter(removeConversation);
    this.activeConversationIds.delete(conversationId);

    if (this.selectedConversation?.conversationId === conversationId) {
      this.selectedConversation = null;
    }

    console.info('Conversation WS: removed conversation outside the current agent scope', {
      conversationId,
      currentAgentId: this.agent?.id ?? this.user?.id ?? null
    });
    this.cdr.markForCheck();
  }

}
